import { ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { Input } from '@/UI/Input/Input';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { useIsHovered } from '@/hooks/useIsHovered';
import { I18N_KEYS } from '@/language/keys';
import { LIST_MAX_CHARS } from '@/components/Nodes/consts/validations.consts';
import { useTextIterator } from '@/hooks/useTextIterator';
import { AddItemButton } from '../../Common/AddItemButton/AddItemButton';

interface DesignAppTextIteratorProps {
  id: string;
  value: { options: string[] };
  onChange: (id: string, value: { options: string[] }) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

export default function DesignAppTextIterator({
  id,
  value,
  onChange,
  disabled = false,
  error,
  helperText,
}: DesignAppTextIteratorProps) {
  const { t } = useTranslation();
  const emit = useCallback((next: { options: string[] }) => onChange(id, next), [id, onChange]);
  const {
    options,
    maxOptions,
    inputRefs,
    handleValueChange,
    handleAddItem,
    handleEnter,
    handleTab,
    handleDelete,
    handleDeleteAndFocusPrev,
  } = useTextIterator({ value, onChange: emit });

  return (
    <FlexCol sx={{ width: '100%', gap: 0.75 }}>
      <FlexCol
        sx={{
          gap: 0.75,
          maxHeight: 240,
          overflow: 'hidden auto',
          py: '2px',
          scrollbarColor: `${color.White16_T} transparent`,
        }}
      >
        {options.map((option, index) => (
          <TextIteratorItem
            key={index}
            item={option}
            index={index}
            onValueChange={handleValueChange}
            onEnter={handleEnter}
            onTab={handleTab}
            inputRef={(el) => {
              inputRefs.current[index] = el;
            }}
            onDelete={handleDelete}
            onDeleteAndFocusPrev={handleDeleteAndFocusPrev}
            editable={!disabled}
            allowDelete={options.length > 1}
            error={error && index === 0}
            helperText={error && index === 0 ? helperText : undefined}
          />
        ))}
      </FlexCol>
      <FlexCenVer sx={{ justifyContent: 'space-between' }}>
        {!disabled && (
          <AddItemButton
            show
            onClick={handleAddItem}
            text={t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.ITERATOR.ADD_ITEM)}
            disabled={options.length >= maxOptions}
          />
        )}
        <TextSmRg color={token.textSecondary}>
          {options.length > maxOptions
            ? t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.ITERATOR.ITEMS_COUNT, { count: options.length })
            : `${options.length}/${maxOptions}`}
        </TextSmRg>
      </FlexCenVer>
    </FlexCol>
  );
}

function TextIteratorItem({
  editable = false,
  item,
  index,
  onValueChange,
  onEnter,
  onTab,
  inputRef,
  onDelete,
  onDeleteAndFocusPrev,
  allowDelete = true,
  error,
  helperText,
}: {
  editable?: boolean;
  item: string;
  index: number;
  onValueChange: (value: string, index: number) => void;
  onEnter: (index: number) => void;
  onTab: (index: number, direction: 'next' | 'prev') => void;
  inputRef: (el: HTMLInputElement | null) => void;
  onDelete: (index: number) => void;
  onDeleteAndFocusPrev: (index: number) => void;
  allowDelete?: boolean;
  error?: boolean;
  helperText?: string;
}) {
  const { t } = useTranslation();
  const { isHovered, ...hoverProps } = useIsHovered();
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => onValueChange(evt.target.value, index),
    [index, onValueChange],
  );
  const handleDelete = useCallback(() => onDelete(index), [index, onDelete]);

  const handleKeyDown = useCallback(
    (evt: React.KeyboardEvent<HTMLInputElement>) => {
      if (!editable) return;

      if (evt.key === 'Escape') {
        evt.preventDefault();
        evt.stopPropagation();
        evt.currentTarget.blur();
        return;
      }

      if (evt.key === 'Tab') {
        evt.preventDefault();
        evt.stopPropagation();
        return onTab(index, evt.shiftKey ? 'prev' : 'next');
      }

      if (evt.key === 'Enter') {
        evt.preventDefault();
        evt.stopPropagation();
        if (item.length === 0) return;
        return onEnter(index);
      }

      if (evt.key === 'Backspace' && item.length === 0 && allowDelete) {
        evt.preventDefault();
        evt.stopPropagation();
        onDeleteAndFocusPrev(index);
      }
    },
    [allowDelete, editable, index, item, onDeleteAndFocusPrev, onEnter, onTab],
  );

  return (
    <FlexCenVer sx={{ width: '100%', height: 32 }} {...hoverProps} className={isFocused ? 'nodrag' : ''}>
      <Input
        fullWidth
        disabled={!editable}
        value={item}
        onChange={handleChange}
        inputProps={{ maxLength: LIST_MAX_CHARS }}
        onKeyDown={handleKeyDown}
        noBlurOnEnter
        placeholder={t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.ITERATOR.PLACEHOLDER, { index: index + 1 })}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        sx={{ pointerEvents: !editable ? 'none' : '' }}
        inputRef={inputRef}
        error={error}
        helperText={helperText}
      />
      {editable && allowDelete && (isHovered || isFocused) ? (
        <AppXBtn onClick={handleDelete} size={20} sx={{ ml: 0.5 }} />
      ) : null}
    </FlexCenVer>
  );
}
